import { useState, useEffect } from 'react';
import { resourceApi, type Resource } from '@/services/api';
import { LinkIcon, PencilIcon, TrashIcon, PlusIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useCanEdit } from '@/contexts/AuthContext';

interface ResourcesTabProps {
  initiativeId: string;
}

interface ResourceForm {
  title: string;
  url: string;
}

const emptyForm: ResourceForm = { title: '', url: '' };

function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed && !/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

export function ResourcesTab({ initiativeId }: ResourcesTabProps) {
  const canEdit = useCanEdit();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<ResourceForm>(emptyForm);
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ResourceForm>(emptyForm);
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, [initiativeId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await resourceApi.list(initiativeId);
      setResources(data);
    } catch {
      setError('Failed to load resources');
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    setAddError(null);
    const title = addForm.title.trim();
    const url = normalizeUrl(addForm.url);
    if (!title) { setAddError('Title is required'); return; }
    if (!url) { setAddError('URL is required'); return; }

    setAdding(true);
    try {
      const created = await resourceApi.create(initiativeId, { title, url });
      setResources((prev) => [...prev, created]);
      setAddForm(emptyForm);
      setShowAddForm(false);
    } catch {
      setAddError('Failed to add resource');
    } finally {
      setAdding(false);
    }
  }

  function startEdit(resource: Resource) {
    setEditingId(resource.id);
    setEditForm({ title: resource.title, url: resource.url });
    setEditError(null);
  }

  async function handleSaveEdit(id: string) {
    setEditError(null);
    const title = editForm.title.trim();
    const url = normalizeUrl(editForm.url);
    if (!title) { setEditError('Title is required'); return; }
    if (!url) { setEditError('URL is required'); return; }

    setSaving(true);
    try {
      const updated = await resourceApi.update(id, { title, url });
      setResources((prev) => prev.map((r) => (r.id === id ? updated : r)));
      setEditingId(null);
    } catch {
      setEditError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await resourceApi.delete(id);
      setResources((prev) => prev.filter((r) => r.id !== id));
    } catch {
      // silently ignore — user can retry
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center text-sm text-red-600">{error}</div>
    );
  }

  return (
    <div className="space-y-3">
      {/* List */}
      {resources.length === 0 && !showAddForm && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
            <LinkIcon className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">No resources yet</p>
          {canEdit && (
            <p className="text-xs text-gray-400 mt-0.5">Add links to docs, designs, or specs</p>
          )}
        </div>
      )}

      {resources.map((resource) => {
        if (editingId === resource.id) {
          return (
            <ResourceEditRow
              key={resource.id}
              form={editForm}
              onChange={setEditForm}
              onSave={() => handleSaveEdit(resource.id)}
              onCancel={() => setEditingId(null)}
              saving={saving}
              error={editError}
            />
          );
        }

        return (
          <div
            key={resource.id}
            className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg group hover:bg-gray-100 transition-colors"
          >
            <LinkIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1 truncate"
              >
                {resource.title}
                <ArrowTopRightOnSquareIcon className="w-3 h-3 flex-shrink-0 opacity-60" />
              </a>
              <p className="text-xs text-gray-400 truncate">{resource.url}</p>
            </div>
            {canEdit && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <button
                  onClick={() => startEdit(resource)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  title="Edit"
                >
                  <PencilIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(resource.id)}
                  disabled={deletingId === resource.id}
                  className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors disabled:opacity-40"
                  title="Delete"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Add form */}
      {showAddForm && canEdit && (
        <ResourceEditRow
          form={addForm}
          onChange={setAddForm}
          onSave={handleAdd}
          onCancel={() => { setShowAddForm(false); setAddForm(emptyForm); setAddError(null); }}
          saving={adding}
          error={addError}
          isNew
        />
      )}

      {/* Add button */}
      {canEdit && !showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add resource
        </button>
      )}
    </div>
  );
}

interface ResourceEditRowProps {
  form: ResourceForm;
  onChange: (form: ResourceForm) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
  isNew?: boolean;
}

function ResourceEditRow({ form, onChange, onSave, onCancel, saving, error, isNew }: ResourceEditRowProps) {
  return (
    <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/40 space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={form.title}
          onChange={(e) => onChange({ ...form, title: e.target.value })}
          placeholder="Title (e.g. Design Spec)"
          className="flex-1 px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          maxLength={200}
          autoFocus={isNew}
        />
      </div>
      <div className="flex gap-2">
        <input
          type="url"
          value={form.url}
          onChange={(e) => onChange({ ...form, url: e.target.value })}
          placeholder="URL (e.g. https://figma.com/...)"
          className="flex-1 px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSave(); } if (e.key === 'Escape') onCancel(); }}
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : isNew ? 'Add' : 'Save'}
        </button>
      </div>
    </div>
  );
}
